import { Card } from 'components/Card';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { isEmail } from 'class-validator';
import { ALERT, NAVIGATE_MODAL_BACK } from 'store/actions';
import { doSetupEngageUpdateGeneral } from 'store/remote/actions';
import { PageHeader } from 'components/PageHeader';

export const EngageSettingsMessagingModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsEngageMessaging>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const [fieldErrors, setFieldErrors] = React.useState<{
    replyToEmail: boolean;
  }>({
    replyToEmail: false,
  });

  const { messaging } = route.params;

  const dataRef = React.useRef(cloneDeep(messaging));
  const isDirty = React.useCallback(
    () => !isEqual(messaging, dataRef.current),
    [messaging],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    const newFieldErrors = { ...fieldErrors };
    const errors: string[] = [];
    const trimmedEmail = dataRef.current.replyToEmail.trim();
    if (trimmedEmail && !isEmail(trimmedEmail)) {
      errors.push('Reply-to email address must be a valid email address');
      newFieldErrors.replyToEmail = true;
    } else {
      newFieldErrors.replyToEmail = false;
    }
    setFieldErrors(newFieldErrors);
    if (errors.length) {
      dispatch(
        ALERT({
          title: 'Cannot Save',
          text: errors.join('\n\n'),
        }),
      );
      return;
    }
    if (isDirty()) {
      dispatch(doSetupEngageUpdateGeneral({ data: dataRef.current }));
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, fieldErrors, isDirty]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
  ) => {
    const newFieldErrors = { ...fieldErrors };
    if (key === 'replyToEmail') {
      const trimmedEmail = value.trim();
      newFieldErrors.replyToEmail = !!trimmedEmail && !isEmail(trimmedEmail);
    }
    dataRef.current[key] = value;
    setFieldErrors(newFieldErrors);
    if (isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Messaging"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        label="Reply-To Email"
        explanation="When Engage sends an email, replies from recipients will go to this address"
        invalid={fieldErrors.replyToEmail}
      >
        <TextField
          iconLeft={ICONS.CircleEnvelopeSolid}
          placeholder="Email Address"
          onChange={(value) => handleChangeValue('replyToEmail', value)}
          value={dataRef.current.replyToEmail}
          onEsc={handleClose}
          autoComplete="email"
          inputMode="email"
          autoCorrect={false}
          autoCapitalize="none"
          autoFocusWeb
          autoFocusDelay
          maxLength={240}
        />
      </Card>
    </Page>
  );
};
